'use client';

import { Button, Skeleton } from '@heroui/react';
import { Icon } from '@iconify/react';
import type { ReactNode } from 'react';
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';

import { CHANNEL_WORDS } from '../channel-words';
import type { CompanyContext } from '../company-context';
import { useContextSuggestions } from '../hooks/useContextSuggestions';
import { useJoinSurveySources } from '../hooks/useJoinSurveySources';
import { JOIN_WORDS } from '../join-survey';
import {
    type Answers,
    answeredBrand,
    buildQuestions,
    LET_DANI_CHOOSE,
    type OptionBadge,
    previewParts,
    promptFromAnswers,
    questionSlots,
    type QuestionOption,
    startingAnswers,
    toggleAnswer,
    type UseCaseQuestion,
} from '../questions';
import { PlanSidebar } from '@/@studio-ai/components/planner-mode/plan-sidebar';
import type { PlanProgressPayload } from '@/@studio-ai/types';

import type { SlotKey } from '../prompt-template';
import type { ConnectUseCase } from '../use-cases';

interface UseCaseQuestionsProps {
    useCase: ConnectUseCase;
    /** Remembered answers, or the org's name as the brand. */
    context: CompanyContext;
    orgName: string;
    /** Every question answered: ask DANI. */
    onSubmit: (prompt: string, answers: Answers) => void;
    /** "Edit the wording": close the card and leave the question in the box. */
    onDismiss: (prompt: string) => void;
    /** Internal staff View-as control, rendered above the questions when the
     * caller's own gating allows it — lets an internal user preview this
     * screen for another org's name instead of leaving the page to find it.
     * Opaque here: this module has no masquerade concept of its own. */
    orgSwitcher?: ReactNode;
}

/**
 * DANI's plan screen for a Connect use case, shown in place of DANI's landing
 * page. The conversation column frames the questions as a short exchange: the
 * use case the user clicked, DANI saying what it will show and why it is
 * asking, then one question per tab with recommended options and an Other box
 * (the shape of Claude's own clarifying questions), with the question DANI will
 * be asked updating under them. DANI's own plan panel sits on the right and
 * fills in as each question is answered. Number keys pick options; Enter
 * moves on.
 */
export function UseCaseQuestions({
    useCase,
    context,
    orgName,
    onSubmit,
    onDismiss,
    orgSwitcher,
}: UseCaseQuestionsProps) {
    const [answers, setAnswers] = useState<Answers>(() => startingAnswers(context, orgName));
    const [other, setOther] = useState<Partial<Record<SlotKey, string>>>({});
    // The survey search waits for a pause in typing, not every keystroke.
    const [surveySearch, setSurveySearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setSurveySearch((other.survey ?? '').trim()), 300);
        return () => clearTimeout(timer);
    }, [other.survey]);
    const [step, setStep] = useState(0);
    // The furthest question reached: everything before it is done.
    const [reached, setReached] = useState(0);
    const cardRef = useRef<HTMLDivElement>(null);

    const slots = useMemo(
        () => questionSlots(useCase.template, context),
        [useCase.template, context],
    );
    const join = useJoinSurveySources({
        enabled: slots.includes('survey'),
        withQuestions: slots.includes('question'),
        channel: useCase.channel,
        surveySearch: surveySearch,
        pickedSurvey: (answers.survey?.[0] ?? '').trim(),
        questionSearch: other.question ?? '',
    });
    // Boxes that search a list rather than take a typed answer: the surveys
    // always, a survey's questions once its questionnaire could be read.
    const searchKeys = useMemo(
        () => new Set<SlotKey>(join.hasQuestionnaire ? ['survey', 'question'] : ['survey']),
        [join.hasQuestionnaire],
    );

    const effective = useMemo(
        () => withOther(answers, other, searchKeys),
        [answers, other, searchKeys],
    );
    // A use case with no brand question is still about the company's brand,
    // which is what its audience and category recommendations are for.
    const brand = slots.includes('brand') ? answeredBrand(effective) : context.brand || orgName;
    // Recommendations follow the brand once the user leaves the brand
    // question, never per keystroke of a typed brand. Fetching starts as the
    // card opens, so they are usually ready by the next question.
    const [suggestFor, setSuggestFor] = useState(brand);
    const { suggestions, isLoading: suggesting } = useContextSuggestions(
        suggestFor,
        useCase.channel,
        slots.some((key) => key === 'competitors' || key === 'audience' || key === 'category'),
    );
    const questions = buildQuestions(useCase.template, context, {
        orgName,
        brand,
        suggestions,
        channelWords: CHANNEL_WORDS[useCase.channel],
        ask: useCase.ask,
        surveys: join.surveys,
        surveyQuestionMatches: join.questionMatches,
        questionSuggestions: join.questionSuggestions,
        hasQuestionnaire: join.hasQuestionnaire,
        joinWords:
            useCase.channel in JOIN_WORDS
                ? JOIN_WORDS[useCase.channel as keyof typeof JOIN_WORDS]
                : undefined,
    });
    const question = questions[step];
    const isLast = step === questions.length - 1;

    if (!question) return null;

    const picked = answers[question.key] ?? [];
    const otherText = other[question.key] ?? '';

    const pick = (option: QuestionOption) => {
        setAnswers((prev) => {
            const next = {
                ...prev,
                [question.key]: toggleAnswer(
                    prev[question.key] ?? [],
                    option.value,
                    question.multiSelect,
                ),
            };
            // A different survey has different questions.
            if (question.key === 'survey' && prev.survey?.[0] !== option.value) {
                next.question = [LET_DANI_CHOOSE];
            }
            return next;
        });
        if (question.key === 'survey') setOther((prev) => ({ ...prev, question: '' }));
        // A single typed answer is either an option or Other, never both.
        if (!question.multiSelect && question.otherMode === 'answer') {
            setOther((prev) => ({ ...prev, [question.key]: '' }));
        }
    };

    const typeOther = (text: string) => {
        setOther((prev) => ({ ...prev, [question.key]: text }));
        // A search only changes what is listed, never the answer.
        if (question.otherMode === 'search' || text.trim() === '') return;
        setAnswers((prev) => ({
            ...prev,
            [question.key]: question.multiSelect
                ? (prev[question.key] ?? []).filter((value) => value !== LET_DANI_CHOOSE)
                : [],
        }));
    };

    const goTo = (target: number) => {
        if (question.key === 'brand') setSuggestFor(brand);
        setStep(target);
        setReached((prev) => Math.max(prev, target));
        cardRef.current?.focus();
    };

    const next = () => {
        if (isLast) onSubmit(promptFromAnswers(useCase.template, effective), effective);
        else goTo(step + 1);
    };

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.target instanceof HTMLInputElement) {
            if (event.key === 'Enter') {
                event.preventDefault();
                next();
            }
            return;
        }
        const index = Number(event.key) - 1;
        if (Number.isInteger(index) && question.options[index]) {
            event.preventDefault();
            pick(question.options[index]);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            next();
        }
    };

    const answered = (q: UseCaseQuestion) => (effective[q.key] ?? []).length > 0;
    const loadingOptions = (key: SlotKey) =>
        ((key === 'competitors' || key === 'audience' || key === 'category') && suggesting) ||
        (key === 'survey' && join.surveysLoading && join.surveys.length === 0) ||
        (key === 'question' &&
            join.questionsLoading &&
            join.questionSuggestions.length === 0 &&
            !other.question);
    const ask = () => onSubmit(promptFromAnswers(useCase.template, effective), effective);
    const preview = previewParts(useCase.template, effective);
    const plan = planFor(useCase, questions, effective, reached, brand);

    return (
        <div className="relative flex h-full bg-[#FAFAFA]">
            <div className="min-w-0 flex-1 overflow-y-auto">
                <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 pb-16 pt-10 text-left">
                    {/* The click, shown as what the user asked for — and,
                        for internal staff, a way to preview this screen for
                        another org before answering. `orgSwitcher`'s own
                        wrapper renders even when empty so `justify-between`
                        still pins the chip to the right. */}
                    <div className="flex items-center justify-between">
                        <div>{orgSwitcher}</div>
                        <span className="flex items-center gap-2 rounded-full bg-default-100 px-4 py-2 text-[14px] text-foreground">
                            <Icon
                                icon={`lucide:${useCase.icon}`}
                                width={15}
                                height={15}
                                className="text-[#1E70FF]"
                            />
                            {useCase.name}
                        </span>
                    </div>

                    {/* DANI says what it will do and why it is asking. */}
                    <div className="flex items-start gap-3">
                        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-mfour-green/15 text-[#0b6b43]">
                            <Icon icon="lucide:sparkles" width={15} height={15} />
                        </span>
                        <p className="pt-0.5 text-[15px] leading-[1.6] text-foreground">
                            I&apos;ll show you {useCase.summary}. First,{' '}
                            {questions.length === 1
                                ? 'one quick question'
                                : `${questions.length} quick questions`}{' '}
                            so it&apos;s about your business. Pick an answer or type your own, or
                            skip them and I&apos;ll use the defaults.
                        </p>
                    </div>

                    <div
                        ref={cardRef}
                        tabIndex={-1}
                        onKeyDown={onKeyDown}
                        className="rounded-2xl border border-divider bg-white shadow-[0_1px_2px_rgba(25,33,56,0.05)] outline-none"
                    >
                        <div className="p-5">
                            <div className="mb-3 flex items-center justify-between gap-3">
                                <span className="text-[12px] font-medium text-default-500">
                                    Question {step + 1} of {questions.length}
                                </span>
                                <div className="flex flex-wrap items-center gap-1.5" role="tablist">
                                    {questions.map((q, i) => (
                                        <button
                                            key={q.key}
                                            type="button"
                                            role="tab"
                                            aria-selected={i === step}
                                            onClick={() => goTo(i)}
                                            className={[
                                                'flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[12px] transition-colors',
                                                i === step
                                                    ? 'border-[#1E70FF]/40 bg-[#1E70FF]/[0.08] text-[#1557cc]'
                                                    : 'border-divider text-default-500 hover:text-foreground',
                                            ].join(' ')}
                                        >
                                            {i < step && answered(q) && (
                                                <Icon icon="lucide:check" width={12} height={12} />
                                            )}
                                            {q.header}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <p className="mb-3 text-[16px] font-semibold text-foreground">
                                {question.question}
                                {question.multiSelect && (
                                    <span className="ml-2 text-[12px] font-normal text-default-500">
                                        Pick any
                                    </span>
                                )}
                            </p>
                            {question.hint && (
                                <p className="-mt-2 mb-3 text-[12px] text-default-500">
                                    {question.hint}
                                </p>
                            )}

                            <div className="flex flex-col gap-2">
                                {question.options.map((option, i) => (
                                    <OptionRow
                                        key={`${question.key}:${option.value}`}
                                        index={i + 1}
                                        option={option}
                                        multiSelect={question.multiSelect}
                                        isSelected={picked.includes(option.value)}
                                        onPress={() => pick(option)}
                                    />
                                ))}
                                {loadingOptions(question.key) && (
                                    <div
                                        className="flex flex-col gap-2"
                                        aria-label="Loading options"
                                    >
                                        <Skeleton className="h-[46px] rounded-lg" />
                                        <Skeleton className="h-[46px] rounded-lg" />
                                    </div>
                                )}
                                <input
                                    aria-label={`Other ${question.header.toLowerCase()}`}
                                    placeholder={
                                        question.otherMode === 'search'
                                            ? question.otherPlaceholder
                                            : `Or type your own: ${question.otherPlaceholder}${question.key === 'competitors' ? ' (separate with commas)' : ''}`
                                    }
                                    value={otherText}
                                    onChange={(event) => typeOther(event.target.value)}
                                    className="h-[42px] rounded-lg border border-divider px-3 text-[14px] outline-none placeholder:text-default-400 focus:border-[#1E70FF]/60"
                                />
                                {question.key === 'survey' &&
                                    surveySearch !== '' &&
                                    join.surveysLoading && (
                                        <span className="text-[12px] text-default-500">
                                            Searching your surveys…
                                        </span>
                                    )}
                            </div>
                        </div>

                        {/* What DANI will be asked, updating with every answer. */}
                        <div className="rounded-b-2xl border-t border-divider bg-default-50 px-5 py-4">
                            <div className="mb-1 flex items-center justify-between gap-3">
                                <span className="text-[12px] font-medium text-default-500">
                                    DANI will ask
                                </span>
                                <button
                                    type="button"
                                    className="text-[12px] text-default-500 underline-offset-2 hover:text-foreground hover:underline"
                                    onClick={() =>
                                        onDismiss(promptFromAnswers(useCase.template, effective))
                                    }
                                >
                                    Edit the wording
                                </button>
                            </div>
                            <p className="text-[14px] leading-[1.6] text-default-700">
                                {preview.map((part, i) =>
                                    part.slot === undefined ? (
                                        <span key={i}>{part.text}</span>
                                    ) : (
                                        <span
                                            key={i}
                                            className={[
                                                part.answered
                                                    ? 'font-semibold text-[#1557cc]'
                                                    : 'text-default-500',
                                                part.slot === question.key
                                                    ? 'rounded bg-[#1E70FF]/[0.08] px-0.5'
                                                    : '',
                                            ].join(' ')}
                                        >
                                            {part.text}
                                        </span>
                                    ),
                                )}
                            </p>

                            <div className="mt-4 flex items-center justify-between gap-3">
                                <Button size="sm" variant="light" className="-ml-3" onPress={ask}>
                                    Skip questions and ask
                                </Button>
                                <div className="flex items-center gap-2">
                                    {step > 0 && (
                                        <Button
                                            size="sm"
                                            variant="light"
                                            onPress={() => goTo(step - 1)}
                                        >
                                            Back
                                        </Button>
                                    )}
                                    {isLast ? (
                                        <Button
                                            size="sm"
                                            className="bg-mfour-green font-semibold text-[#0b3f29]"
                                            startContent={
                                                <Icon
                                                    icon="lucide:sparkles"
                                                    width={14}
                                                    height={14}
                                                />
                                            }
                                            onPress={ask}
                                        >
                                            Ask DANI
                                        </Button>
                                    ) : (
                                        <Button size="sm" color="primary" onPress={next}>
                                            Next
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <PlanSidebar payload={plan} humanizeAnswers={false} />
        </div>
    );
}

/** DANI's plan panel for these questions: one step per question, done once
 * the user has moved past it, showing the picked options by their labels. */
function planFor(
    useCase: ConnectUseCase,
    questions: UseCaseQuestion[],
    answers: Answers,
    reached: number,
    brand: string,
): PlanProgressPayload {
    return {
        templateName: useCase.name,
        // Only a use case that asks about a brand is about one.
        brand: questions.some((q) => q.key === 'brand') ? brand || undefined : undefined,
        totalSteps: questions.length,
        steps: questions.map((q, i) => {
            const labels = (answers[q.key] ?? []).map(
                (value) => q.options.find((option) => option.value === value)?.label ?? value,
            );
            const answer = labels.filter(Boolean).join(', ');
            return {
                id: q.key,
                label: q.header,
                question: q.question,
                answer: answer || null,
                status: i < reached ? (answer ? 'answered' : 'skipped') : 'pending',
            };
        }),
    };
}

/** Answers with each typed Other folded in (commas split typed competitors
 * into several names). A box that searches (`searchKeys`) never answers. */
function withOther(
    answers: Answers,
    other: Partial<Record<SlotKey, string>>,
    searchKeys: ReadonlySet<SlotKey>,
): Answers {
    const out: Answers = { ...answers };
    for (const [key, text] of Object.entries(other) as Array<[SlotKey, string]>) {
        const typed = text.trim();
        if (!typed || searchKeys.has(key)) continue;
        if (key === 'competitors') {
            const names = typed
                .split(',')
                .map((name) => name.trim())
                .filter(Boolean);
            out[key] = [...(answers[key] ?? []), ...names];
        } else if (key === 'question') {
            out[key] = [...(answers[key] ?? []).filter((v) => v !== LET_DANI_CHOOSE), typed];
        } else {
            out[key] = [typed];
        }
    }
    return out;
}

const BADGE_CLASSES: Record<OptionBadge, string> = {
    Recommended: 'bg-mfour-green/15 text-[#0b6b43]',
    'Last used': 'bg-default-100 text-default-600',
    'Your company': 'bg-default-100 text-default-600',
};

function OptionRow({
    index,
    option,
    multiSelect,
    isSelected,
    onPress,
}: {
    index: number;
    option: QuestionOption;
    multiSelect: boolean;
    isSelected: boolean;
    onPress: () => void;
}) {
    return (
        <button
            type="button"
            role={multiSelect ? 'checkbox' : 'radio'}
            aria-checked={isSelected}
            onClick={onPress}
            className={[
                'flex items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                isSelected
                    ? 'border-[#1E70FF]/50 bg-[#1E70FF]/[0.06]'
                    : 'border-divider hover:border-default-400',
            ].join(' ')}
        >
            <span
                className={[
                    'mt-0.5 flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center border text-[11px]',
                    multiSelect ? 'rounded' : 'rounded-full',
                    isSelected
                        ? 'border-[#1E70FF] bg-[#1E70FF] text-white'
                        : 'border-default-300 text-default-400',
                ].join(' ')}
            >
                {isSelected ? <Icon icon="lucide:check" width={12} height={12} /> : index}
            </span>
            <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2 text-[14px] text-foreground">
                    {option.label}
                    {option.badge && (
                        <span
                            className={`rounded px-1.5 py-px text-[11px] font-medium ${BADGE_CLASSES[option.badge]}`}
                        >
                            {option.badge}
                        </span>
                    )}
                </span>
                {option.description && (
                    <span className="mt-0.5 block text-[12px] text-default-500">
                        {option.description}
                    </span>
                )}
            </span>
        </button>
    );
}

UseCaseQuestions.displayName = 'UseCaseQuestions';
